import { useContext, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNotificationContext } from 'units/notifications/NotificationContext'
import FetchContext from '../../context/fetch/FetchContext'
import { withFlag } from '../contacts/contactLists'
import { messageOf } from '../Message'
import MarketingWithdrawalDialog from './MarketingWithdrawalDialog'
import useMarketingConsent from './useMarketingConsent'

export function withDecision(details, decided) {
    const marketing = (decided?.flags ?? []).includes('marketing')

    return (details ?? []).map((detail) => detail.id !== decided?.id
        ? detail
        : {
            ...detail,
            consent: decided.consent,
            flags: withFlag(detail.flags, 'marketing', marketing),
        })
}

export default function useMarketingDecision({
    details,
    consentPathOf,
    onDecided,
    onFlagChange,
}) {
    const fetcher = useContext(FetchContext)
    const { addNotification } = useNotificationContext()

    const decisionMutation = useMutation({
        mutationFn: ({ detailId, marketing, consent }) => fetcher.authenticatedFetch({
            method: 'post',
            path: consentPathOf(detailId),
            data: {
                data: {
                    marketing,
                    ...(consent != null ? { consent } : {}),
                },
            },
        }),
        onSuccess: onDecided,
        onError: (error) => {
            addNotification({
                type: 'error',
                content: messageOf(error),
            })
        },
    })

    const applyFlagChange = (id, key, wanted, consent) => {
        if (key === 'marketing') {
            return decisionMutation.mutateAsync({
                detailId: id,
                marketing: wanted,
                consent,
            })
        }

        const applied = onFlagChange(id, key, wanted)

        return Promise.resolve(applied)
    }

    const { onFlagChange: requestFlagChange, dialog } = useMarketingConsent({
        details,
        onFlagChange: applyFlagChange,
    })

    const [withdrawing, setWithdrawing] = useState(null)

    const requestWithConfirmation = (id, key, wanted) => {
        if (key !== 'marketing' || wanted) {
            return requestFlagChange(id, key, wanted)
        }

        const detail = (details ?? []).find((one) => one.id === id) ?? { id }

        return new Promise((resolve, reject) => {
            setWithdrawing({ detail, resolve, reject })
        })
    }

    const confirmWithdrawal = () => {
        const answered = withdrawing
        setWithdrawing(null)

        requestFlagChange(answered.detail.id, 'marketing', false)
            .then(answered.resolve, answered.reject)
    }

    const declineWithdrawal = () => {
        const answered = withdrawing
        setWithdrawing(null)
        answered.reject(new Error('withdrawal declined'))
    }

    const withdrawalDialog = (
        <MarketingWithdrawalDialog
            open={withdrawing != null}
            detail={withdrawing?.detail}
            onConfirm={confirmWithdrawal}
            onDecline={declineWithdrawal}
        />
    )

    return {
        onFlagChange: requestWithConfirmation,
        dialog: (
            <>
                {dialog}
                {withdrawalDialog}
            </>
        ),
        deciding: decisionMutation.isPending,
    }
}
